/**
 * THE INVESTIGATION CONTINUES — the Archive's permanent V2.0 navigation
 * signature. Quiet footer on every investigative page offering 2-3
 * documented branches from the relationship graph. Never algorithmic,
 * never inferred — the evidence is the guide.
 *
 * Also exports BranchCard, shared with the shared-trail page.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, User, Building2, Crosshair, FileText, MapPin, CalendarDays,
} from "lucide-react";
import { getStack } from "./investigationMemory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BRANCH_ICONS = {
  Person: User, Organization: Building2, "Government Agency": Building2,
  Company: Building2, Network: Building2, Operation: Crosshair,
  Program: Crosshair, Dossier: FileText, Document: FileText,
  Place: MapPin, Event: CalendarDays,
};

export const BranchCard = ({ branch, index, testPrefix = "continue-branch" }) => {
  const Icon = BRANCH_ICONS[branch.type_label] || FileText;
  const img = branch.image_url
    ? (branch.image_url.startsWith("http") ? branch.image_url : `${BACKEND}${branch.image_url}`)
    : null;
  return (
    <div className="border flex flex-col" style={{ borderColor: "#2D2A26", background: "#12110E" }}
         data-testid={`${testPrefix}-${index}`}>
      <div className="h-36 flex items-center justify-center border-b overflow-hidden"
           style={{ borderColor: "#221F1B", background: "#0D0C0A" }}>
        {img ? (
          <img src={img} alt={branch.title}
               className="h-full w-full object-cover object-top opacity-90" loading="lazy" />
        ) : (
          <Icon size={30} className="text-[#3A3630]" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#A68F5E]">
          {branch.type_label}
        </div>
        <div className="font-display text-[18px] text-[#E4DFD3] mt-1 leading-snug">
          {branch.title}
        </div>
        <p className="text-[12px] text-[#A39E93] mt-2 leading-relaxed flex-1">
          {branch.sentence}
        </p>
        <div className="flex items-center gap-3 mt-3">
          <span className="font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5 border"
                style={{ borderColor: "#3A3630", color: "#A68F5E" }}>
            {branch.evidence_label}
          </span>
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#5A554C]">
            {branch.meta}
          </span>
        </div>
        <Link to={branch.url}
              data-testid={`${testPrefix}-continue-${index}`}
              className="mt-4 inline-flex items-center gap-2 font-mono text-[9px] uppercase
                         tracking-[0.3em] border px-3 py-2 text-[#E4DFD3] self-start
                         hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
              style={{ borderColor: "#3A3630" }}>
          Continue Investigation <ArrowRight size={10} />
        </Link>
      </div>
    </div>
  );
};

const INVESTIGATIVE = /^\/(topic|people|organizations|operations|connections)\/[a-z0-9-]+$/;

export const InvestigationContinues = ({ path: pathProp }) => {
  const location = useLocation();
  const path = pathProp || location.pathname;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!INVESTIGATIVE.test(path)) { setData(null); return undefined; }
    let dead = false;
    setData(null);
    const exclude = getStack().slice(-5).map((e) => e.url.split("?")[0]).join(",");
    axios.get(`${API}/continue`, { params: { path, exclude } })
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ branches: [], explore_url: "/connections" }); });
    return () => { dead = true; };
  }, [path]);

  if (!INVESTIGATIVE.test(path) || !data) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-16 border-t"
             style={{ borderColor: "#221F1B" }}
             data-testid="investigation-continues-section">
      <div className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
        Continue Your Investigation
      </div>
      <p className="text-[12px] text-[#8A8377] mt-2">
        If you're learning about this topic for the first time, these are the
        best places to continue — documented evidence and historical
        relationships are the guide.
      </p>
      {data.branches.length === 0 ? (
        <div className="mt-6 border border-dashed p-8 text-center"
             style={{ borderColor: "#2D2A26" }} data-testid="continue-branches-empty">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#8A8377]">
            No further verified branches currently mapped
          </div>
          <div className="text-[12px] text-[#5A554C] mt-2">
            The Archive continues to expand.
          </div>
          <Link to={data.explore_url || "/connections"}
                data-testid="continue-branches-explore-btn"
                className="mt-5 inline-flex items-center gap-2 font-mono text-[9px] uppercase
                           tracking-[0.3em] border px-4 py-2 text-[#E4DFD3]
                           hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
                style={{ borderColor: "#3A3630" }}>
            Explore Related Investigations
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.branches.map((b, i) => <BranchCard key={b.url} branch={b} index={i} />)}
        </div>
      )}
    </section>
  );
};
